import React, { useState, useEffect } from 'react';
import { LuX, LuUserPlus, LuMail, LuUser, LuBriefcase, LuEdit } from "react-icons/lu";
import axios from 'axios';
import { baseurl } from '../../baseurl';
import { AlertCircle, CheckCircle, Edit } from 'lucide-react';

const TeamModal = ({ isOpen, onClose, onSuccess, editData }) => {
    const [form, setForm] = useState({ name: '', email: '', role: 'Estimator' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ text: '', type: '' });

    // Handle Data population and cleanup
    useEffect(() => {
        if (isOpen) {
            setMsg({ text: '', type: '' });
            if (editData) {
                // If editing, fill the form with existing data
                setForm({
                    name: editData.name || '',
                    email: editData.invited_email || editData.registered_email || '',
                    role: editData.role || 'Estimator'
                });
            } else {
                // If adding, start with a fresh form
                setForm({ name: '', email: '', role: 'Estimator' });
            }
        }
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMsg({ text: '', type: '' });
        
        const userToken = localStorage.getItem('userToken');

        try {
            let res;
            if (editData) {
                // UPDATE EXISTING MEMBER
                res = await axios.put(`${baseurl}updateTeamMember/${editData.id}`, form, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
            } else {
                // ADD NEW MEMBER
                res = await axios.post(`${baseurl}addTeamMember`, form, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
            }

            if (res.data.success) {
                setMsg({ 
                    text: res.data.message || `Member ${editData ? 'updated' : 'added'} successfully!`, 
                    type: 'success' 
                });
                
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1500);
            }
        } catch (err) {
            setMsg({ 
                text: err.response?.data?.message || 'Action failed. Please try again.', 
                type: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tm-modal-overlay">
            <div className="tm-modal-content">
                <div className="tm-modal-header">
                    <div className="d-flex align-items-center">
                        <div className="tm-header-icon">
                            {editData ? <Edit /> : <LuUserPlus />}
                        </div>
                        <div>
                            <h5 className="mb-0">{editData ? 'Edit Team Member' : 'Add Team Member'}</h5>
                            <small className="text-muted">
                                {editData ? `Managing ${editData.name}` : 'Invite via email address'}
                            </small>
                        </div>
                    </div>
                    <button className="tm-close-btn" onClick={onClose}><LuX /></button>
                </div>

                <form onSubmit={handleSubmit} className="tm-modal-body">
                    {msg.text && (
                        <div className={`tm-alert tm-alert-${msg.type}`}>
                            {msg.type === 'success' ? <CheckCircle className="me-2" size={18} /> : <AlertCircle className="me-2" size={18} />}
                            {msg.text}
                        </div>
                    )}

                    <div className="tm-input-group">
                        <label className="tm-label"><LuUser className="me-1"/> Full Name</label>
                        <input 
                            type="text" 
                            className="tm-input" 
                            placeholder="John Doe" 
                            required 
                            value={form.name}
                            onChange={(e) => setForm({...form, name: e.target.value})}
                        />
                    </div>

                    <div className="tm-input-group mt-3">
                        <label className="tm-label"><LuMail className="me-1"/> Email Address</label>
                        <input 
                            type="email" 
                            className="tm-input" 
                            placeholder="john@example.com" 
                            required 
                            value={form.email}
                            onChange={(e) => setForm({...form, email: e.target.value})}
                            disabled={!!editData} // Email usually shouldn't be changed for existing users
                            style={editData ? { background: '#f8f9fa', cursor: 'not-allowed' } : {}}
                        />
                        {!editData && <div className="tm-info-text">Existing users will be linked instantly.</div>}
                    </div>

                    <div className="tm-input-group mt-3">
                        <label className="tm-label"><LuBriefcase className="me-1"/> Assign Role</label>
                        <select 
                            className="tm-select"
                            value={form.role}
                            onChange={(e) => setForm({...form, role: e.target.value})}
                        >
                            <option value="Estimator">Estimator</option>
                            <option value="Field Manager">Field Manager</option>
                            <option value="Admin">Admin</option>
                        </select>
                    </div>

                    <div className="tm-modal-footer">
                        <button type="button" className="tm-btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="tm-btn-submit" disabled={loading}>
                            {loading ? 'Processing...' : editData ? 'Update Member' : 'Send Invitation'}
                        </button>
                    </div>
                </form>
            </div>
            
            <style>
                {`
                .tm-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0, 0, 0, 0.4);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    backdrop-filter: blur(4px);
                }

                .tm-modal-content {
                    background: #fff;
                    width: 100%;
                    max-width: 450px;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                    animation: tmFadeUp 0.3s ease-out;
                }

                @keyframes tmFadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .tm-modal-header {
                    padding: 20px;
                    border-bottom: 1px solid #F4F7FE;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .tm-header-icon {
                    width: 40px; height: 40px;
                    background: #4318FF;
                    color: #fff;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 12px;
                    font-size: 20px;
                }

                .tm-close-btn {
                    background: #F4F7FE;
                    border: none;
                    border-radius: 50%;
                    width: 30px; height: 30px;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer;
                }

                .tm-modal-body { padding: 20px; }

                .tm-alert {
                    padding: 12px;
                    border-radius: 12px;
                    margin-bottom: 20px;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                }

                .tm-alert-error {
                    background: #FFF5F5;
                    color: #E31A1A;
                    border: 1px solid #FED7D7;
                }

                .tm-alert-success {
                    background: #F0FFF4;
                    color: #2F855A;
                    border: 1px solid #C6F6D5;
                }

                .tm-label {
                    display: block;
                    font-size: 14px;
                    font-weight: 600;
                    color: #2B3674;
                    margin-bottom: 8px;
                }

                .tm-input, .tm-select {
                    width: 100%;
                    padding: 12px;
                    border: 1px solid #D1D9E8;
                    border-radius: 12px;
                    font-size: 14px;
                    outline: none;
                    transition: 0.2s;
                }

                .tm-input:focus { border-color: #4318FF; box-shadow: 0 0 0 3px rgba(67, 24, 255, 0.1); }

                .tm-info-text { font-size: 11px; color: #707EAE; margin-top: 6px; }

                .tm-modal-footer {
                    padding-top: 20px;
                    display: flex;
                    gap: 12px;
                }

                .tm-btn-cancel {
                    flex: 1;
                    background: #F4F7FE;
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-weight: 600;
                    color: #2B3674;
                    cursor: pointer;
                }

                .tm-btn-submit {
                    flex: 2;
                    background: #4318FF;
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-weight: 600;
                    color: #fff;
                    cursor: pointer;
                }

                .tm-btn-submit:disabled {
                    background: #A3AED0;
                    cursor: not-allowed;
                }
                `}
            </style>
        </div>
    );
};

export default TeamModal;